/** The Sandbox mark of a grid's Tenant cell (plan D8).
 *
 * A sandbox is a tenant of its own (`<production code>sbx`), so a grid that shows a
 * production operator and its sandbox side by side would otherwise show two tenants
 * with the same name. Every Tenant cell marks a sandbox row:
 *
 * - `<TenantMark environment>` — the flask and the word "Sandbox" as a small outlined
 *   badge (the `EnvironmentBadge` language, sized like the grid's Franchise/Licensee
 *   badge: 18px high, 11px text). Nothing for a production operator or an unknown
 *   environment: every operator is production unless it says otherwise. Never colour
 *   alone — the word is always there.
 * - `<WithTenantMark environment placement>` — a cell's content with the mark in its
 *   badge slot: `under` the name where the row grows to fit (auto-height grids, the
 *   bookings/orders badge line), `beside` it in a fixed 36px compact row, where a
 *   second line does not fit. A production row renders its content untouched.
 * - `<TenantLinkCell tenant>` — the catalog grids' Operator cell: the code (else the
 *   name) linking to the operator's page, with the mark beside it.
 */
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Box, Chip, Stack, Tooltip, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { TourOperatorEnvironment } from '@/api/tourOperators';

/** What a Tenant cell knows of its tenant's environment (`Tour_Operator.kind`). */
export type TenantEnvironment = TourOperatorEnvironment | null | undefined;

export const SANDBOX_MARK_TOOLTIP = 'A sandbox: its own tenant for developing an integration.';

const MARK_SX = {
  height: 18,
  fontSize: 11,
  flex: 'none',
  '& .MuiChip-icon': { fontSize: 13, ml: '5px', mr: '-3px' },
} as const;

export function TenantMark({
  environment,
  sx,
}: {
  environment?: TenantEnvironment;
  sx?: SxProps<Theme>;
}) {
  if (environment !== 'sandbox') return null;
  return (
    <Tooltip describeChild title={SANDBOX_MARK_TOOLTIP}>
      <Chip
        size="small"
        variant="outlined"
        icon={<ScienceOutlinedIcon />}
        label="Sandbox"
        data-environment="sandbox"
        sx={[MARK_SX, ...(Array.isArray(sx) ? sx : [sx])]}
      />
    </Tooltip>
  );
}

/** A Tenant cell's content with the Sandbox mark in its badge slot. */
export function WithTenantMark({
  environment,
  placement = 'beside',
  sx,
  children,
}: {
  environment?: TenantEnvironment;
  /** `under`: the badge line below the name (rows that grow); `beside`: one line. */
  placement?: 'beside' | 'under';
  /** Merged into the container — e.g. `{ py: 0 }` in a grid whose cells pad themselves. */
  sx?: SxProps<Theme>;
  children: ReactNode;
}) {
  if (environment !== 'sandbox') return <>{children}</>;
  const extra = Array.isArray(sx) ? sx : [sx];
  if (placement === 'under') {
    return (
      <Stack spacing={0.25} sx={[{ py: 0.75, minWidth: 0 }, ...extra]}>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
        <TenantMark environment={environment} sx={{ alignSelf: 'flex-start' }} />
      </Stack>
    );
  }
  return (
    <Box
      sx={[
        { display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, height: '100%' },
        ...extra,
      ]}
    >
      <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {children}
      </Box>
      <TenantMark environment={environment} />
    </Box>
  );
}

/** A tenant as a catalog grid's Operator cell knows it. */
export interface TenantCellRef {
  id: number;
  code?: string | null;
  name: string;
  environment?: TenantEnvironment;
}

/** The catalog grids' Operator cell: the tenant's code (else its name) as a link to its
 *  page — the click stays the link's, never the row's — with the Sandbox mark beside it.
 *  `empty` is what a row without a tenant shows ("—", "System"). */
export function TenantLinkCell({
  tenant,
  empty = '—',
}: {
  tenant: TenantCellRef | null | undefined;
  empty?: ReactNode;
}) {
  if (!tenant) return <>{empty}</>;
  return (
    <WithTenantMark environment={tenant.environment}>
      <RouterLink
        to={`/tour-operators/${tenant.id}`}
        style={{ color: '#0a1a3f' }}
        onClick={(e) => e.stopPropagation()}
      >
        {tenant.code || tenant.name}
      </RouterLink>
    </WithTenantMark>
  );
}
